<template>
  <div style="height: 286px">
    <v-date-picker-header
      next-icon="$keyboard_arrow_right_rounded"
      prev-icon="$keyboard_arrow_left_rounded"
      :color="color ? color : 'var(--accent)'"
      :min="minDate.split('-')[0]"
      :max="maxDate"
      :value="pickerYear"
      @input="onChangeYearQuarter"
    ></v-date-picker-header>

    <div class="quarter-container">
      <z-card
        v-for="quarter in quarters"
        :key="quarter.value"
        width="100%"
        :dark="quarter.active"
        :color="quarter.active ? `${color ? color : 'var(--midblue-500)'}` : ''"
        :disabled="setDisabledQuarter(quarter)"
        @click="setActiveQuarter(quarter)"
      >
        <template #content>
          <div class="quarter-item">
            <div class="title">Q{{ quarter.value }}</div>
            <div class="months">
              {{ quarterMonths[quarter.value].join(',') }}
            </div>
          </div>
        </template>
      </z-card>
    </div>
  </div>
</template>

<script>
import {formatISO, getQuarter, parseISO, startOfToday} from 'date-fns'

export default {
  props: {
    minDate: {
      type: String,
      required: true,
    },
    maxDate: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
    color: {
      type: String,
    },
  },

  data() {
    return {
      pickerYear: '',

      quarters: [
        {
          value: 1,
          active: false,
        },
        {
          value: 2,
          active: false,
        },
        {
          value: 3,
          active: false,
        },
        {
          value: 4,
          active: false,
        },
      ],
    }
  },

  computed: {
    quarterMonths() {
      return {
        1: [this.$t('january'), this.$t('february'), this.$t('march')],
        2: [this.$t('april'), this.$t('may'), this.$t('june')],
        3: [this.$t('july'), this.$t('august'), this.$t('september')],
        4: [this.$t('october'), this.$t('november'), this.$t('december')],
      }
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(value) {
        const [year, quarter] = value.split('-')

        this.pickerYear = year

        this.quarters = this.quarters.map((i) => {
          if (i.value === Number(quarter)) {
            return {
              ...i,
              active: true,
            }
          }

          return {
            ...i,
            active: false,
          }
        })
      },
    },
  },

  i18n: {
    messages: {
      pt: {
        january: 'Jan',
        february: 'Fev',
        march: 'Mar',
        april: 'Abr',
        may: 'Mai',
        june: 'Jun',
        july: 'Jul',
        august: 'Ago',
        september: 'Set',
        october: 'Out',
        november: 'Nov',
        december: 'Dez',
      },
      en: {
        january: 'Jan',
        february: 'Feb',
        march: 'Mar',
        april: 'Apr',
        may: 'May',
        june: 'Jun',
        july: 'Jul',
        august: 'Aug',
        september: 'Sep',
        october: 'Oct',
        november: 'Nov',
        december: 'Dec',
      },
      es: {
        january: 'Ene',
        february: 'Feb',
        march: 'Mar',
        april: 'Abr',
        may: 'May',
        june: 'Jun',
        july: 'Jul',
        august: 'Ago',
        september: 'Sep',
        october: 'Oct',
        november: 'Nov',
        december: 'Dic',
      },
      fr: {
        january: 'Jan',
        february: 'Fév',
        march: 'Mar',
        april: 'Avr',
        may: 'Mai',
        june: 'Juin',
        july: 'Juil',
        august: 'Août',
        september: 'Sep',
        october: 'Oct',
        november: 'Nov',
        december: 'Déc',
      },
      tr: {
        january: 'Oca',
        february: 'Şub',
        march: 'Mar',
        april: 'Nis',
        may: 'May',
        june: 'Haz',
        july: 'Tem',
        august: 'Ağu',
        september: 'Eyl',
        october: 'Eki',
        november: 'Kas',
        december: 'Ara',
      },
    },
  },

  methods: {
    setDisabledQuarter(quarter) {
      const [year, month] = formatISO(startOfToday(), {
        representation: 'date',
      }).split('-')

      const [minYear] = this.minDate.split('-')

      const minDateFormatted = parseISO(this.minDate)
      const minDateQuarter = getQuarter(minDateFormatted)

      if (quarter.value < minDateQuarter && this.pickerYear == minYear)
        return true

      if (year !== this.pickerYear) return false
      if (quarter.value === 1) return month < 4
      if (quarter.value === 2) return month < 7
      if (quarter.value === 3) return month < 10
      if (quarter.value === 4) return month < 12

      return false
    },

    setActiveQuarter(quarter) {
      this.quarters = this.quarters.map((i) => {
        if (i.value === quarter.value) {
          return {
            ...i,
            active: true,
          }
        }

        return {
          ...i,
          active: false,
        }
      })

      this.$emit('input', `${this.pickerYear}-${quarter.value}`)
    },

    onChangeYearQuarter(year) {
      this.quarters = this.quarters.map((quarter) => {
        return {
          ...quarter,
          active:
            `${year.toString()}-${quarter.value.toString()}` === this.value,
        }
      })

      this.pickerYear = year
    },
  },
}
</script>

<style scoped lang="scss">
.quarter-container {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr;

  .quarter-item {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .months {
      font-size: 12px;
      line-height: 14px;
      font-weight: 400;
      color: var(--gray-500);
    }
  }
}
</style>
